import {Badge, PushButton, Table, TableBody, TableCell, TableHead, TableRow, useAsyncEffect} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {Controller} from "../controller/Controller";
import {Lock, LockStatus} from "@variocube/app-api";
import {naturalCompare} from "@variocube/app-common";
import {useLocalization} from "../../i18n";

interface LockListViewProps {
    controller: Controller;
}

export function LockListView({controller}: LockListViewProps) {
    const [locks, setLocks] = useState<Lock[]>([]);
    useAsyncEffect(async () => setLocks(sortLocks(await controller.getLocks())), [controller]);

    const handleLockStatusChanged = useCallback((changedLock: string, status: LockStatus) => {
        setLocks(sortLocks([
            ...(locks || []).filter(lock => lock.handle != changedLock),
            {handle: changedLock, status}
        ]));
    }, [locks]);

    useEffect(() => {
        controller.addLockStatusListener(handleLockStatusChanged);
        return () => controller.removeLockStatusListener(handleLockStatusChanged)
    }, [controller, handleLockStatusChanged]);

    const handleOpen = useCallback((lock: string) => controller.open(lock), [controller]);

    const {t} = useLocalization();
    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>{t("Lock")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Open")}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {locks?.map(lock => (
                    <LockRow
                        key={lock.handle}
                        lock={lock}
                        onOpen={handleOpen}
                    />
                ))}
            </TableBody>
        </Table>
    );
}

function LockRow({lock, onOpen}: { lock: Lock; onOpen: (lock: string) => any }) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();
    const handleOpenClick = useCallback(() => onOpen(lock.handle), [onOpen, lock]);

    return (
        <TableRow key={lock.handle}>
            <TableCell>{lock.handle}</TableCell>
            <TableCell><Badge>{lock.status}</Badge></TableCell>
            <TableCell>
                <PushButton primary onClick={handleOpenClick}>
                    {t("Open")}
                </PushButton>
            </TableCell>
        </TableRow>
    );
}

function sortLocks(locks: Lock[]) {
    return locks.sort((x, y) => naturalCompare(x.handle, y.handle));
}